import React from 'react';
import { FlatList, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { calcSizeForDevice } from 'utils/index';

import BandshopImage from '../../../assets/bandshop.png';
import SectionArrows from '../components/SectionArrows';
import { useHorizontalPagination } from '../helpers/pagination';
import BandshopCard from './BandshopCard';

import { useProgram } from 'components/Program/context';

export type BandshopProductItem = {
  name: string;
  link: string;
  image: string;
  isDefault?: boolean;
};

type SectionBandshopProps = {
  items: BandshopProductItem[];
};

const DEFAULT_CARD: BandshopProductItem = {
  name: 'Explore mais em Bandshop',
  link: 'https://www.bandshop.com.br/',
  image: BandshopImage,
};

const SectionBandshop: React.FC<SectionBandshopProps> = ({ items }: SectionBandshopProps) => {
  const program = useProgram();
  const analytics = useAnalytics();

  const filteredBandshopItems = items
    .filter(({ name, link, image }) => name && link && image)
    .slice(0, 15);

  const productItems =
    filteredBandshopItems.length > 0
      ? [...filteredBandshopItems, { ...DEFAULT_CARD, isDefault: true }]
      : [];

  const { ref, onScroll, goForward, renderItem, goBackward, canGoForward, canGoBackward } =
    useHorizontalPagination({
      items: productItems,
      renderItem: ({ item }: { item: BandshopProductItem }) => {
        const { name, link, image, isDefault } = item;

        return (
          <View style={{ marginRight: calcSizeForDevice(10) }}>
            <BandshopCard name={name} link={link} image={image} isDefault={isDefault} />
          </View>
        );
      },
    });

  const onPress = (side: 'left' | 'right') => {
    analytics.triggerCarouselBandshopArrowScrollActionEvent({
      scrollSide: side,
      programId: program?._id,
      programTitle: program?.title,
      programAgeRating: program?.rating,
    });

    side === 'left' ? goBackward() : goForward();
  };

  const carouselMobileScrollEvent = () => {
    analytics.triggerCarouselBandshopMobileScrollActionEvent({
      programId: program?._id,
      programTitle: program?.title,
      programAgeRating: program?.rating,
    });
  };

  if (!productItems) return null;

  return (
    <View style={{ position: 'relative' }}>
      <FlatList
        ref={ref}
        data={productItems}
        onScroll={onScroll}
        onScrollEndDrag={carouselMobileScrollEvent}
        maxToRenderPerBatch={3}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
        horizontal={true}
        keyExtractor={(item, index) => `${item.link}_${index.toString()}`}
      />
      <SectionArrows side="left" onPress={() => onPress('left')} isVisible={canGoBackward} />

      <SectionArrows side="right" onPress={() => onPress('right')} isVisible={canGoForward} />
    </View>
  );
};

export default SectionBandshop;
